import { OnlineSales } from 'gw-edge-client';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import type {
    EditTermDTO,
    EditTermPriceDTO,
    OPCTermDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { useEffect, useMemo, useState } from 'react';
import { useOpcInfomation } from './useOpcInfomation';
import type { AdjustCoverageAction } from '../pages/OpcEntryPage/types';
import { getTermOPCInfoSelector } from '../recoil/selectors/getTermOPCInfo';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import { isOpcFetchingAtom } from '../recoil/atoms/isOpcFetchingAtom';
import { jobAtom } from '../recoil/atoms/jobAtom';
import { DRAFT } from '../recoil/types/types';

export function useAdjustCoverageOPC(): {
    termInfo: OPCTermDTO | undefined;
    editTermDTO: EditTermDTO | undefined;
    editTermPriceDTO: EditTermPriceDTO | undefined;
    updateTermValue: (value: number) => void;
    updateTermStringValue: (value: string) => void;
    adjustAction: AdjustCoverageAction | undefined;
    isAdjustableDataValid: boolean;
    validateAdjustableDeductible: () => boolean;
    revertChange: () => Promise<void>;
} {
    const { termChange } = useOpcInfomation();
    const setIsOpcFetching = useSetRecoilState(isOpcFetchingAtom);
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const { productName, coverableId, coverageId, termId, adjustAction } = termChange;
    const [editTermDTO, setEditTermDTO] = useState<EditTermDTO>({
        coverableId,
        productName,
        coverageId,
        termId
    });
    const [editTermPriceDTO, setEditTermPriceDTO] = useState<EditTermPriceDTO | undefined>();
    const [isAdjustableDataValid, setIsAdjustableDataValid] = useState(true);

    // Recoil will not use its cache utilize if editTermDTOJson is re-declare
    const editTermDTOJson = useMemo(
        () => JSON.stringify(editTermDTO),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const termOPCInfo = useRecoilValue(getTermOPCInfoSelector(editTermDTOJson));
    const { jobNumber, sessionId } = useRecoilValue(jobInfoAtom);

    if (!jobNumber) {
        throw new Error('Invalid jobNumber');
    }
    if (!sessionId) {
        throw new Error('Invalid sessionId');
    }
    useEffect(() => {
        setIsOpcFetching(true);
        setIsAdjustableDataValid(true);
        if (termOPCInfo) {
            // Only run for the first time when editTermDTO.termValue not bind data from termOPCInfo.directValue yet
            if (editTermDTO.termValue === undefined) {
                editTermDTO.termValue = termOPCInfo.directValue;
            }
            if (editTermDTO.termStringValue === undefined) {
                editTermDTO.termStringValue = termOPCInfo.options?.find(
                    (term) => term.name === termOPCInfo.value
                )?.code;
            }

            OnlineSales.calculatePriceForEditedCoverageOPC(jobNumber, sessionId, editTermDTO)
                .then((response) => {
                    setEditTermPriceDTO(response);
                    setJobData({ ...jobData, jobStatus: DRAFT });
                    setIsOpcFetching(false);
                })
                .catch((e) => {
                    if (!e.isHttpTimeout_Top) throw e;
                    setIsOpcFetching(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editTermDTO]);

    const revertChange = async (): Promise<void> => {
        setIsOpcFetching(true);
        const oldEditTermDTO = {
            ...editTermDTO,
            termValue: Number(termOPCInfo?.value),
            isUpdateOnly: false
        };
        await OnlineSales.calculatePriceForEditedCoverageOPC(jobNumber, sessionId, oldEditTermDTO);
        setIsOpcFetching(false);
    };

    const updateTermValue = (value: number, publicId?: string): void => {
        if (value === editTermDTO.termValue) return;
        setEditTermDTO((prev) => {
            setIsOpcFetching(true);
            return {
                ...prev,
                termValue: value,
                termId: publicId ?? prev.termId
            };
        });
    };

    const updateTermStringValue = (value: string): void => {
        if (value === editTermDTO.termStringValue) return;
        setEditTermDTO((prev) => {
            return {
                ...prev,
                termStringValue: value
            };
        });
    };

    const validateAdjustableDeductible = (): boolean => {
        let isValid = isAdjustableDataValid;
        if (termOPCInfo?.directValue === editTermPriceDTO?.newTermValue?.amount) {
            isValid = false;
        }

        if (isValid !== isAdjustableDataValid) {
            setIsAdjustableDataValid(isValid);
        }
        return isValid;
    };

    return {
        termInfo: termOPCInfo,
        editTermDTO,
        editTermPriceDTO,
        updateTermValue,
        updateTermStringValue,
        adjustAction,
        isAdjustableDataValid,
        validateAdjustableDeductible,
        revertChange
    };
}
